import i18next from 'i18next'
import {
  Fun,
  Updater,
  initialRoute,
  unloadedAsyncState,
} from 'widgets-for-react'
import {
  AuthState,
  defaultAuthState,
} from '../../authentication/auth_widget/authState'
import { isLoggedIn } from '../../authentication/auth_widget/utils'
import { loadUserSubscriptions } from '../../subscription/subscriptionApi'
import {
  SubscriptionState,
  initialSubscriptionState,
  reloadedSubscriptionState,
} from '../../subscription/subscriptionState'
import { CurrentPage } from '../../utils/routes'
import { BlogOverviewState } from './blogs/blogOverview/blogOverviewState'
import { CollectionDetailState } from './collections/collectionDetail/collectionDetailState'
import {
  getCollections,
  loadCollections,
} from './collections/collectionsLoader/collectionsLoaderApi'
import {
  CollectionsLoaderState,
  initialCollectionsLoaderState,
} from './collections/collectionsLoader/collectionsLoaderState'
import { ComponentDetailState } from './components/componentDetail/componentDetailState'
import { ComponentEditState } from './components/componentEdit/componentEditState'
import { ComponentOverviewState } from './components/componentOverview/componentOverviewState'
import {
  ComponentsCountLoaderState,
  initialComponentsCountLoaderState,
} from './components/componentsCountLoader/componentsCountLoaderState'
import { ContactState, contactFormInputs } from './contact/contactState'
import { DashboardState } from './dashboard/dashboardState'
import { DishOverviewState } from './dishes/dishOverview/dishOverviewState'
import {
  FiltersLoaderState,
  initialFiltersLoaderState,
} from './filters/filtersLoader/filtersLoaderState'
import { FooterState, defaultFooterState } from './footer/footerState'
import { HeaderState, defaultHeaderState } from './header/headerState'
import { ExtendedHomeState, HomeState } from './home/home.state'
import {
  HubspotTrackingState,
  initialHubspotTrackingState,
} from './hubspotTracking/hubspotTrackingState'
import { KnowledgeTypeOverviewState } from './knowledge/knowledgeTypeOverview/knowledgeTypeOverviewState'
import { KnowledgeTypeTeasersState } from './knowledge/knowledgeTypeTeasers/knowledgeTypeTeasersState'
import { MetadataState, initialMetadataState } from './metadata/metadata.state'
import { Pages, initialPage } from './routes/routesState'
import { routesArray } from './routes/routesUtils'
import { SearchState } from './search/searchState'
import { SeasonalCalendarState } from './seasonalCalendar/seasonalCalendarState'
import { SharepointPageState } from './sharepointPage/sharepointPageState'
import { StudentSubscriptionState } from './studentSubscription/studentSubscriptionState'
import {
  ViewListenerState,
  initialViewListenerState,
} from './viewListener/viewListenerState'
import { StopSubscriptionState } from '../../subscription/stopSubscription/stopSubscriptionState'

export type RootState = {
  page: CurrentPage<Pages>
  collectionsLoaderState: CollectionsLoaderState
  authState: AuthState
  footer: FooterState
  header: HeaderState
  subscriptionState: SubscriptionState
  hubspotTrackingState: HubspotTrackingState
  filtersLoaderState: FiltersLoaderState
  componentsCountLoaderState: ComponentsCountLoaderState
  viewListenerState: ViewListenerState
  metadataState: MetadataState
  isPrintMode: boolean
}

export const initialRootState = (): RootState => {
  const rootState: RootState = {
    page: initialPage,
    collectionsLoaderState: initialCollectionsLoaderState,
    authState: defaultAuthState,
    footer: defaultFooterState,
    header: defaultHeaderState,
    subscriptionState: initialSubscriptionState,
    hubspotTrackingState: initialHubspotTrackingState,
    filtersLoaderState: initialFiltersLoaderState,
    componentsCountLoaderState: initialComponentsCountLoaderState,
    viewListenerState: initialViewListenerState,
    metadataState: initialMetadataState,
    isPrintMode: false,
  }
  const updater = initialRoute(routesArray(i18next.language))
  return updater ? updater(rootState) : rootState
}

export const rootStateUpdaters = {
  updateHeaderState:
    (headerStateUpdater: Updater<HeaderState>) =>
    (state: RootState): RootState => ({
      ...state,
      header: headerStateUpdater(state.header),
    }),
  updateFooterState:
    (
      footerStateUpdater: Fun<
        FooterState,
        FooterState
      >
    ) =>
    (state: RootState): RootState => {
      const newFooterState = footerStateUpdater(state.footer)
      return {
        ...state,
        footer: newFooterState
      }
    },
  updateAuthState:
    (authStateUpdater: Updater<AuthState>) =>
    (state: RootState): RootState => {
      const newAuthState = authStateUpdater(state.authState)
      return isLoggedIn(newAuthState)
        ? {
            ...state,
            authState: newAuthState,
            collectionsLoaderState:
              state.collectionsLoaderState.kind == 'unloaded'
                ? loadCollections(getCollections, state.collectionsLoaderState)
                : state.collectionsLoaderState,
            subscriptionState: {
              ...state.subscriptionState,
              userSubscriptions:
                state.subscriptionState.userSubscriptions.kind == 'unloaded'
                  ? loadUserSubscriptions()
                  : state.subscriptionState.userSubscriptions,
            },
          }
        : {
            ...state,
            authState: newAuthState,
            collectionsLoaderState: unloadedAsyncState(),
            subscriptionState: {
              ...state.subscriptionState,
              userSubscriptions: unloadedAsyncState(),
            },
          }
    },
  updateCollectionsState:
    (updater: Updater<CollectionsLoaderState>) =>
    (state: RootState): RootState => ({
      ...state,
      collectionsLoaderState: updater(state.collectionsLoaderState),
    }),
  homeStateUpdater:
    (updater: Fun<HomeState, ExtendedHomeState>) =>
    (state: RootState): RootState => {
      if (state.page.kind == 'home') {
        const {
          reloadCollections,
          searchString,
          ...newHomeState
        } = updater(state.page.pageState)
        return {
          ...state,
          page: { ...state.page, pageState: newHomeState },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
          header:
            searchString == undefined
              ? state.header
              : { ...state.header, searchString: searchString },
        }
      } else return state
    },
  componentOverviewStateUpdater:
    (
      updater: Fun<
        ComponentOverviewState,
        ComponentOverviewState & { reloadCollections?: boolean }
      >
    ) =>
    (state: RootState): RootState => {
      if (state.page.kind === 'componentOverview') {
        const { reloadCollections, ...newComponentOverviewState } = updater(
          state.page.pageState
        )
        return {
          ...state,
          page: { ...state.page, pageState: newComponentOverviewState },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
        }
      } else return state
    },
  updateBlogOverviewState:
    (blogOverviewStateUpdater: Updater<BlogOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind == 'blogOverview'
          ? {
              ...state.page,
              pageState: blogOverviewStateUpdater(state.page.pageState),
            }
          : state.page,
    }),
  updateComponentDetailState:
    (
      componentDetailStateUpdater: Fun<
        ComponentDetailState,
        ComponentDetailState & { reloadCollections?: boolean }
      >
    ) =>
    (state: RootState): RootState => {
      if (state.page.kind == 'componentDetail') {
        const { reloadCollections, ...newComponentDetailState } =
          componentDetailStateUpdater(state.page.pageState)
        return {
          ...state,
          page: { ...state.page, pageState: newComponentDetailState },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
        }
      } else return state
    },
  updateComponentEditState:
    (updater: Updater<ComponentEditState>) =>
    (state: RootState): RootState =>
      state.page.kind == 'componentEdit'
        ? {
            ...state,
            page: { ...state.page, pageState: updater(state.page.pageState) },
          }
        : state,
  updateSearchState:
    (
      searchStateUpdater: Fun<
        SearchState,
        SearchState & { reloadCollections?: boolean }
      >
    ) =>
    (state: RootState): RootState => {
      if (state.page.kind == 'search') {
        const { reloadCollections, ...newSearchState } = searchStateUpdater(
          state.page.pageState
        )
        return {
          ...state,
          page: { ...state.page, pageState: newSearchState },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
        }
      } else return state
    },
  updateDashboardState:
    (
      dashboardStateUpdater: Fun<
        DashboardState,
        DashboardState & { reloadCollections?: boolean }
      >
    ) =>
    (state: RootState): RootState => {
      if (state.page.kind == 'dashboard') {
        const { reloadCollections, ...newDashboardState } =
          dashboardStateUpdater(state.page.pageState)
        return {
          ...state,
          page: {
            ...state.page,
            pageState: newDashboardState,
          },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
        }
      } else return state
    },
  updateCollectionDetailState:
    (
      collectionDetailStateUpdater: Fun<
        CollectionDetailState,
        CollectionDetailState & { reloadCollections?: boolean }
      >
    ) =>
    (state: RootState): RootState => {
      if (state.page.kind == 'collectionDetail') {
        const { reloadCollections, ...newCollectionDetailState } =
          collectionDetailStateUpdater(state.page.pageState)
        return {
          ...state,
          page: { ...state.page, pageState: newCollectionDetailState },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
        }
      } else return state
    },
  updateKnowledgeOverviewState:
    (knowledgeOverviewUpdater: Updater<KnowledgeTypeTeasersState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind == 'knowledgeOverview'
          ? {
              ...state.page,
              pageState: knowledgeOverviewUpdater(state.page.pageState),
            }
          : state.page,
    }),
  updateKnowledgeTypeOverviewState:
    (updater: Updater<KnowledgeTypeOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind == 'knowledgeTypeOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updatePageState:
    (
      updater: Fun<
        SharepointPageState,
        SharepointPageState & { reloadCollections?: boolean }
      >
    ) =>
    (state: RootState): RootState => {
      if (
        state.page.kind == 'knowledgeDetail' ||
        state.page.kind == 'blogDetail' ||
        state.page.kind == 'dishesDetail' ||
        state.page.kind == 'page'
      ) {
        const { reloadCollections, ...newSharepointPageState } = updater(
          state.page.pageState
        )
        return {
          ...state,
          page: { ...state.page, pageState: newSharepointPageState },
          collectionsLoaderState: reloadCollections
            ? loadCollections(getCollections, state.collectionsLoaderState)
            : state.collectionsLoaderState,
        }
      } else return state
    },
  updateSeasonalCalendarState:
    (updater: Updater<SeasonalCalendarState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind == 'seasonalCalendar'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateContactState:
    (updater: Updater<ContactState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind == 'contact'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateSubscriptionState:
    (subscriptionUpdater: Updater<SubscriptionState>) =>
    (state: RootState): RootState => {
      const newSubscriptionState = subscriptionUpdater(state.subscriptionState)
      const currentPage = state.page
      return {
        ...state,
        subscriptionState: newSubscriptionState,
        page:
          newSubscriptionState.userSubscriptions.kind === 'loaded'
            ? currentPage.kind === 'contact' &&
              contactFormInputs.every(
                (field) =>
                  currentPage.pageState.formInput[field] === undefined &&
                  currentPage.pageState.showFormInputValidation[field] === false
              )
              ? {
                  ...currentPage,
                  pageState: {
                    ...currentPage.pageState,
                    formInput: {
                      ...currentPage.pageState.formInput,
                      name: newSubscriptionState.userSubscriptions.value.User
                        .Name,
                      surname:
                        newSubscriptionState.userSubscriptions.value.User
                          .Surname,
                      email:
                        newSubscriptionState.userSubscriptions.value.User.Email,
                    },
                  },
                }
              : currentPage.kind === 'studentSubscription' &&
                !(
                  currentPage.pageState.subscribeExistingUserState.kind ===
                    'loaded' ||
                  currentPage.pageState.subscribeNewUserState.kind === 'loaded'
                )
              ? {
                  ...currentPage,
                  pageState: {
                    ...currentPage.pageState,
                    formInput: {
                      ...currentPage.pageState.formInput,
                      name: newSubscriptionState.userSubscriptions.value.User
                        .Name,
                      surname:
                        newSubscriptionState.userSubscriptions.value.User
                          .Surname,
                      email:
                        newSubscriptionState.userSubscriptions.value.User.Email,
                    },
                  },
                }
              : state.page
            : state.page,
      }
    },
  updateStudentSubscriptionState:
    (
      updater: Fun<
        StudentSubscriptionState,
        StudentSubscriptionState & { authStateUpdater?: Updater<AuthState> }
      >
    ) =>
    (state: RootState): RootState => {
      if (state.page.kind == 'studentSubscription') {
        const newStudentSubscriptionsState = updater(state.page.pageState)
        return {
          ...state,
          page: {
            ...state.page,
            pageState: newStudentSubscriptionsState,
          },
          subscriptionState:
            newStudentSubscriptionsState.subscribeExistingUserState.kind ===
              'loaded' ||
            newStudentSubscriptionsState.subscribeNewUserState.kind === 'loaded'
              ? reloadedSubscriptionState
              : state.subscriptionState,
          authState: newStudentSubscriptionsState.authStateUpdater
            ? newStudentSubscriptionsState.authStateUpdater(state.authState)
            : state.authState,
        }
      } else return state
    },
  updateSubscriptionStopPaymentState:
    (updater: Updater<StopSubscriptionState>) => 
    (state: RootState): RootState => {
      if (state.page.kind == 'subscriptionStopPayment') {
        const newStopSubscriptionState = updater(state.page.pageState)

        return {
          ...state,
          page: {
            ...state.page,
            pageState: newStopSubscriptionState,
          }
        }
      } 
      else
        return state
    },
  updateHubspotTrackingState:
    (updater: Updater<HubspotTrackingState>) =>
    (state: RootState): RootState => ({
      ...state,
      hubspotTrackingState: updater(state.hubspotTrackingState),
    }),
  updateDishesOverviewState:
    (dishesOverviewStateUpdater: Updater<DishOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind == 'dishesOverview'
          ? {
              ...state.page,
              pageState: dishesOverviewStateUpdater(state.page.pageState),
            }
          : state.page,
    }),
  filtersLoaderStateUpdater:
    (updater: Updater<FiltersLoaderState>) =>
    (state: RootState): RootState => ({
      ...state,
      filtersLoaderState: updater(state.filtersLoaderState),
    }),
  componentsCountLoaderStateUpdater:
    (updater: Updater<ComponentsCountLoaderState>) =>
    (state: RootState): RootState => ({
      ...state,
      componentsCountLoaderState: updater(state.componentsCountLoaderState),
    }),
  viewListenerStateUpdater:
    (updater: Updater<ViewListenerState>) =>
    (state: RootState): RootState => ({
      ...state,
      viewListenerState: updater(state.viewListenerState),
    }),
  updateMetadataState:
    (updater: Updater<MetadataState>) =>
    (state: RootState): RootState => ({
      ...state,
      metadataState: updater(state.metadataState),
    }),
  languageChanged: (state: RootState): RootState => ({
    ...state,
    metadataState: initialMetadataState,
  }),
}
